import { useEffect, useState, useRef, FC, RefObject } from "react";
import { Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, Box, FormControlLabel, Checkbox, Alert, AlertTitle, InputAdornment, FormHelperText } from "@mui/material";
import { PaperProps } from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import { nameValidation, normalizeString } from "utils/Validations";
import dayjs from "dayjs";
import { countries } from 'utils/Countries';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers-pro';
import { TimekeeperService } from "services/TimekeeperService";

interface DuplicateHolidayProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: any;
    reloading: () => void,
    selected: any;
    setSelected: () => void;
}

interface HolidayResponse {
    status: number;
    data: any;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const DuplicateHoliday: FC<DuplicateHolidayProps> = ({ open, setOpen, rows, reloading, selected, setSelected }) => {
    const [name, setName] = useState('')
    const [public_name, setPublicName] = useState('')
    const [year, setYear] = useState(dayjs().year())
    const [country, setCountry] = useState('')
    const [is_renounceable, setIsRenounceable] = useState(true)
    const [is_local, setIsLocal] = useState(true)
    const [start, setStart] = useState(dayjs().utc().startOf('day'))
    const [end, setEnd] = useState(dayjs().utc().startOf('day').add(1, 'day'))
    const [alerts, setAlerts] = useState(false)
    const [loading, setLoading] = useState(false)


    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()

    useEffect(() => {
        if (open) {
            setName(selected[0]?.name || '')
            setPublicName(selected[0]?.public_name || '')
            setYear(selected[0]?.year || dayjs().year())
            setCountry(selected[0]?.country || '')
            setIsRenounceable(selected[0]?.is_renounceable || false)
            setIsLocal(selected[0]?.is_local || false)
            setStart(dayjs.utc(selected[0]?.start_date) || dayjs.utc().startOf('day'))
            setEnd(dayjs.utc(selected[0]?.end_date) || dayjs.utc().startOf('day').add(1, 'day'))
        }
    }, [open, selected]);

    const existName = (name: string) => {
        const normalizedInput = normalizeString(name);
        return rows.some((row: any) => normalizeString(row.name) === normalizedInput);
    };

    const closeForm = () => {
        setOpen(false)
        setName('')
        setPublicName('')
        setYear(dayjs().year())
        setCountry('')
        setIsRenounceable(true)
        setIsLocal(true)
        setStart(dayjs().utc().startOf('day'))
        setEnd(dayjs().utc().startOf('day').add(1, 'day'))
        setAlerts(false)
        setSelected()
        setLoading(false)
    }

    const changes = () => {
        return name !== '' ||
        country !== '' ||
        !dayjs(start).isValid() ||
        !dayjs(end).isValid() ||
        dayjs(start).isAfter(dayjs(end)) ? setAlerts(true) : closeForm()
    }

    const DuplicateHoliday = async () => {
        const tk = new TimekeeperService()
        setLoading(true)
        const data = {
            holiday_id: selected[0].id,
            name: name,
            public_name: public_name,
            year: year,
            country: country,
            is_renounceable: is_renounceable,
            is_local: is_local,
            start: dayjs(start).utc().toDate(),
            end: dayjs(end).utc().toDate(),
        }
        try {
            const response = await tk.holiday.createHoliday(data) as HolidayResponse
            console.log('response:', response)
            if (response.status === 201) {
                enqueueSnackbar(t('settings.holidays.alerts.edit_holiday.message'), { variant: 'success' })
                reloading()
                closeForm()
            } else {
                enqueueSnackbar(t('settings.holidays.alerts.error_edit_holiday.message'), { variant: 'error' })
            }
        } catch (error) {
            console.error('Error creating holiday:', error)
            enqueueSnackbar(t('settings.holidays.alerts.error_edit_holiday.message'), { variant: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const disabled = () => {
        return name === '' ||
        public_name === '' ||
        existName(name) ||
        country === '' ||
        !dayjs(start).isValid() ||
        !dayjs(end).isValid() ||
        dayjs(start).isAfter(dayjs(end));
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes();
            }}
            PaperComponent={PaperComponent}
            maxWidth="sm"
            fullWidth
            aria-labelledby="draggable-dialog-title"
            aria-describedby="draggable-alert-dialog-description"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{t('settings.holidays.modals.titles.duplicate')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            name="name"
                            label={t('settings.holidays.modals.inputs.name')}
                            value={name}
                            autoComplete="off"
                            onChange={(e) => {
                                const newName = e.target.value
                                if (nameValidation(newName)) {
                                    setName(newName)
                                    existName(newName)
                                }
                            }}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${name.length}/100`}</InputAdornment>
                                }
                            }}
                            required
                            fullWidth
                            error={existName(name)}
                        />
                        <FormHelperText
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 0.5,
                                px: 1.75
                            }}
                        >
                            <span>{existName(name) ? t('settings.holidays.modals.alerts.name_exists') : ''}</span>
                        </FormHelperText>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            name="public_name"
                            label={t('settings.holidays.modals.inputs.public_name')}
                            value={public_name}
                            autoComplete="off"
                            onChange={(e) => setPublicName(e.target.value)}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${public_name.length}/100`}</InputAdornment>
                                }
                            }}
                            required
                            fullWidth
                        />
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                            <DatePicker
                                sx={{ width: '100%' }}
                                views={['year']}
                                label={t('settings.holidays.modals.inputs.year')}
                                name="year"
                                value={dayjs(year)}
                                onChange={(date) => {
                                    if (dayjs(date).isValid()) {
                                        const newYear = dayjs(date).year();
                                        setYear(newYear);
                                        setStart(dayjs.utc(start).year(newYear))
                                        setEnd(dayjs.utc(end).year(newYear))
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        required: true,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                        <FormControl required fullWidth>
                            <InputLabel id="country-select-label">{t('settings.holidays.modals.inputs.country')}</InputLabel>
                            <Select
                                fullWidth
                                labelId="country-select-label"
                                id="country-select"
                                name="country"
                                label={t('settings.holidays.modals.inputs.country')}
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country.code} value={country.code}>
                                        <Box
                                            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                        >
                                            <img
                                                loading="lazy"
                                                width="30"
                                                src={`https://flagcdn.com/${country.code.toLowerCase()}.svg`}
                                                alt=""
                                            />
                                            {country.label} ({country.code})
                                        </Box>
                                    </MenuItem>
                                ))}

                            </Select>
                        </FormControl>
                    </Grid>

                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label={t('settings.holidays.modals.inputs.start')}
                                name="start"
                                onChange={(dateStart) => {
                                    if (dayjs(dateStart).isValid()) {
                                        setStart(dayjs.utc(dateStart))
                                        setYear(dayjs(dateStart).year())
                                        setEnd(dayjs.utc(dateStart).add(1, 'day').startOf('day'))
                                    }
                                }}
                                value={dayjs(start).isValid() ? dayjs(start) : undefined}
                                slotProps={{ textField: { required: true } }}
                                ampm={false}
                                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label={t('settings.holidays.modals.inputs.end')}
                                name="end"
                                onChange={(dateEnd) => {
                                    if (dayjs(dateEnd).isValid()) {
                                        setEnd(dayjs.utc(dateEnd))
                                    }
                                }}
                                value={dayjs.utc(end).isValid() ? dayjs.utc(end) : undefined}
                                slotProps={{ textField: { required: true } }}
                                ampm={false}
                                timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                                minDate={dayjs.utc(start).isValid() ? dayjs.utc(start) : undefined}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <FormControlLabel
                                control={<Checkbox
                                    name="is_renounceable"
                                    checked={is_renounceable}
                                    onChange={(e) => setIsRenounceable(e.target.checked)}
                                />}
                                label={t('settings.holidays.modals.inputs.is_renounceable')}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                            <FormControlLabel
                                control={<Checkbox
                                    name="is_local"
                                    checked={is_local}
                                    onChange={(e) => setIsLocal(e.target.checked)}
                                />}
                                label={t('settings.holidays.modals.inputs.is_local')}
                            />
                        </Grid>
                    </LocalizationProvider>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => changes()} color="error" variant="contained" disableElevation>
                    {t('settings.holidays.modals.buttons.cancel')}
                </Button>
                <Button onClick={() => DuplicateHoliday()} color="primary" disabled={disabled()} variant="contained" disableElevation loading={loading}>
                    {t('settings.holidays.modals.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { DuplicateHoliday }